import React, { useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Typography
} from '@mui/material';
import {
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

export interface FilterOption {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'multiselect';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

interface FilterBarProps {
  filters: FilterOption[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onClear: () => void;
  onApply?: () => void;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  values,
  onChange,
  onClear,
  onApply,
  collapsible = false,
  defaultExpanded = true
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const hasActiveFilters = Object.values(values).some(value => 
    value !== '' && value !== null && value !== undefined && 
    (Array.isArray(value) ? value.length > 0 : true)
  );

  const renderFilter = (filter: FilterOption) => {
    switch (filter.type) {
      case 'text':
        return (
          <TextField
            key={filter.key}
            label={filter.label}
            placeholder={filter.placeholder}
            value={values[filter.key] || ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
        );

      case 'select':
        return (
          <FormControl key={filter.key} size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{filter.label}</InputLabel>
            <Select
              value={values[filter.key] || ''}
              label={filter.label}
              onChange={(e) => onChange(filter.key, e.target.value)}
            >
              <MenuItem value="">Toate</MenuItem>
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            key={filter.key}
            label={filter.label}
            type="date"
            value={values[filter.key] || ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        );

      case 'multiselect':
        return (
          <FormControl key={filter.key} size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{filter.label}</InputLabel>
            <Select
              multiple
              value={values[filter.key] || []}
              label={filter.label}
              onChange={(e) => onChange(filter.key, e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return null;
    }
  };

  const content = (
    <Box sx={{ 
      display: 'flex', 
      gap: 2, 
      alignItems: 'center', 
      flexWrap: 'wrap',
      p: 2,
      backgroundColor: 'background.default',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider'
    }}>
      {filters.map(renderFilter)}
      
      <Box display="flex" gap={1} ml="auto">
        {onApply && (
          <Tooltip title="Aplica filtrele">
            <IconButton
              size="small"
              onClick={onApply}
              sx={{ 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'primary.light' }
              }}
            >
              <FilterList />
            </IconButton>
          </Tooltip>
        )}
        
        {hasActiveFilters && (
          <Tooltip title="Reseteaza filtrele">
            <IconButton
              size="small"
              onClick={onClear}
              sx={{ 
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.light' }
              }}
            >
              <Clear />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  if (collapsible) {
    return (
      <Box>
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1}
          onClick={() => setExpanded(!expanded)}
          sx={{ 
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <FilterList fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            Filtre
          </Typography>
          {hasActiveFilters && (
            <Chip 
              label={Object.keys(values).filter(key => values[key]).length}
              size="small"
              color="primary"
            />
          )}
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Box>
        
        <Collapse in={expanded}>
          {content}
        </Collapse>
      </Box>
    );
  }

  return content;
};

export default FilterBar;
